import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Send, Volume2, VolumeX, X } from "lucide-react";
import { sfx } from "@/lib/sound";

const API = process.env.REACT_APP_BACKEND_URL;

const sessionId = () => {
  let id = localStorage.getItem("idevra_areta_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("idevra_areta_session", id);
  }
  return id;
};

export default function AretaWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(() => localStorage.getItem("idevra_areta_voice") !== "0");
  const voiceRef = useRef(voice);
  const speechRef = useRef(null);
  const endRef = useRef(null);
  const loadedRef = useRef(false);

  const stopSpeech = () => {
    if (speechRef.current) {
      speechRef.current.pause();
      speechRef.current = null;
    }
  };

  const toggleVoice = () => {
    sfx.tick();
    setVoice((v) => {
      const next = !v;
      voiceRef.current = next;
      localStorage.setItem("idevra_areta_voice", next ? "1" : "0");
      if (!next) stopSpeech();
      return next;
    });
  };

  const speak = async (text) => {
    try {
      const res = await fetch(`${API}/api/areta/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !voiceRef.current) return;
      const blob = await res.blob();
      stopSpeech();
      const a = new Audio(URL.createObjectURL(blob));
      speechRef.current = a;
      a.play().catch(() => {});
    } catch {
      /* voice is best-effort */
    }
  };

  useEffect(() => {
    if (!open) stopSpeech();
  }, [open]);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${API}/api/areta/history/${sessionId()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMsgs(d.messages.map((m) => ({ role: m.role, text: m.text })));
        else
          setMsgs([
            {
              role: "assistant",
              text: "ARETA online. I can guide you through the ventures, the IP vault, the founder's story — or point investors to Open Comms. How can I help?",
            },
          ]);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    sfx.tick();
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/areta/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const ev = JSON.parse(payload);
          const chunk = ev.delta || ev.error || "";
          if (ev.delta) full += ev.delta;
          if (chunk)
            setMsgs((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
              return copy;
            });
        }
      }
      if (full && voiceRef.current) speak(full);
    } catch {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Comms interference — please retry." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        data-testid="areta-open-btn"
        aria-label="Talk to ARETA"
        onClick={() => {
          sfx.clank();
          setOpen((o) => !o);
        }}
        className="iv-glass fixed bottom-16 right-4 z-[70] flex h-12 w-12 items-center justify-center rounded-full text-[#0284C7] shadow-lg sm:bottom-20 sm:right-6"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
      >
        <Bot size={20} />
        <span className="absolute -top-0.5 right-0 h-2 w-2 rounded-full bg-[#38BDF8] shadow-[0_0_6px_#38BDF8]" />
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="areta-panel"
            className="iv-glass fixed bottom-32 right-4 z-[70] flex h-[26rem] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl shadow-2xl sm:bottom-36 sm:right-6"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
          >
            <div className="flex items-center justify-between border-b border-white/60 px-4 py-3">
              <span className="flex items-center gap-2">
                <Bot size={14} color="#0284C7" />
                <span className="f-syncopate text-[10px] tracking-[0.3em] text-[#0F172A]">ARETA</span>
                <span className="f-mono text-[7px] tracking-[0.2em] text-[#94A3B8]">CONSOLE AI</span>
              </span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="areta-voice-toggle"
                  title={voice ? "Mute Areta's voice" : "Unmute Areta's voice"}
                  onClick={toggleVoice}
                  className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                    voice ? "text-[#0284C7]" : "text-[#94A3B8]"
                  } hover:text-[#0284C7]`}
                >
                  {voice ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <button
                  type="button"
                  data-testid="areta-close-btn"
                  onClick={() => {
                    sfx.back();
                    setOpen(false);
                  }}
                  className="flex h-7 w-7 items-center justify-center text-[#64748B] hover:text-[#0284C7]"
                >
                  <X size={14} />
                </button>
              </span>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="areta-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-[12px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#0284C7] text-white"
                        : "border border-[#E2E8F0] bg-white/70 text-[#334155]"
                    }`}
                  >
                    {m.text || (busy && i === msgs.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <form
              className="flex items-center gap-2 border-t border-white/60 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                data-testid="areta-input"
                className="iv-input flex-1 !py-2 text-[12px]"
                placeholder="Ask Areta…"
                value={input}
                maxLength={2000}
                onChange={(e) => setInput(e.target.value)}
              />
              <button
                type="submit"
                data-testid="areta-send-btn"
                disabled={busy || !input.trim()}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0284C7] text-white transition-colors hover:bg-[#0369A1] disabled:opacity-50"
              >
                <Send size={13} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
